import { createId } from "../../app/runtimeContext";
import type { ObservabilityClient } from "../../observability/types";
import type { EventBus } from "../events/eventBus";
import type {
  EnterprisePermissionEvaluator,
  PermissionSubject,
} from "../permissions/permissionModel";
import type { TaskEngine } from "../tasks/taskEngine";
import type { CyanIntelligenceRepository } from "./repositories";
import type { ExecutionPlan, ExecutionRecord } from "./types";

export class ExecutionCoordinator {
  constructor(
    private readonly repository: CyanIntelligenceRepository,
    private readonly permissions: EnterprisePermissionEvaluator,
    private readonly tasks: TaskEngine,
    private readonly eventBus: EventBus,
    private readonly observability: ObservabilityClient,
  ) {}

  async coordinate(
    plan: ExecutionPlan,
    subject: PermissionSubject,
  ): Promise<ExecutionRecord> {
    const decision = this.permissions.evaluate(
      subject,
      {
        scope: "task",
        organizationId: plan.organizationId,
        productId: plan.productId,
      },
      "create",
    );
    if (!decision.allowed) {
      await this.observability.warn({
        category: "cyan",
        eventName: "cyan_execution_blocked",
        message: `Execution blocked: ${decision.reason}`,
      });
      const blocked: ExecutionRecord = {
        id: createId("execution"),
        organizationId: plan.organizationId,
        planId: plan.id,
        status: "blocked",
        createdTaskIds: [],
        knowledgeEntryIds: [],
        reflectionIds: [],
        createdAt: new Date().toISOString(),
      };
      await this.repository.saveExecution(blocked);
      return blocked;
    }

    const createdTaskIds: string[] = [];
    for (const objective of plan.objectives) {
      for (const plannedTask of objective.tasks) {
        const task = await this.tasks.createTask({
          organizationId: plan.organizationId,
          productId: plan.productId ?? "product_ss-core",
          title: plannedTask.title,
          description: plannedTask.description,
          status: "Planning",
          dependencies: plannedTask.dependencies,
          owner: subject.email,
          memoryLinks: [],
        });
        createdTaskIds.push(task.id);
      }
    }
    const record: ExecutionRecord = {
      id: createId("execution"),
      organizationId: plan.organizationId,
      planId: plan.id,
      status: "coordinated",
      createdTaskIds,
      knowledgeEntryIds: [],
      reflectionIds: [],
      createdAt: new Date().toISOString(),
    };
    await this.repository.saveExecution(record);
    await this.eventBus.publish({
      id: createId("event"),
      name: "TaskCreated",
      payload: record,
      timestamp: record.createdAt,
      correlationId: createId("exec"),
    });
    await this.observability.event({
      category: "cyan",
      eventName: "cyan_execution_coordinated",
      message: `Coordinated ${createdTaskIds.length} tasks.`,
    });
    return record;
  }
}
